import React, { useState, useEffect, useCallback, useRef } from 'react'
import { toastr } from 'react-redux-toastr'
import { useDispatch, useSelector } from 'react-redux'
import _ from 'lodash'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import CustomButton from '../../CustomButton'
import SearchField from "./SearchField";
import ManageAssetsContainer from '../../smart/menuComponentsContainer/ManageAssetsContainer'
import AddAssetContainer from '../../smart/menuComponentsContainer/AddAssetContainer'
import ModalDeviceOfflineDisabled from '../../../pages/vehicles/ModalDeviceOfflineDisabled'
import ModalChangeConfBatch from '../../smart/modals/ModalChangeConfBatch'
import ModalFormatSDBatch from '../../smart/modals/ModalFormatSDBatch'
import ModalDevicesMigration from '../../smart/modals/ModalDevicesMigration'

import { getCompanyById } from '../../../action/CompaniesActions'
import { getDevicesList } from '../../../action/DeviceActions'
import { detectDeviceStatus } from '../../helpers/helperVehicle'
import { isInternalUser } from '../../helpers/constants'
import { isCustomUserPermissions, sendGA } from '../../helpers/helperUsers'
import { exportIcon } from "../../helpers/iconHelpers";

import './ManageVehicles.sass'
import FilterMenu from "../../FilterMenu";

const ManageAssets = (props) => {
	const assetsContainerRef = useRef();
	const { t, i18n } = useTranslation('translation', { keyPrefix: 'manageVehiclesPage.manageAssets' });
	const { selectProfile } = props;
	const dispatch = useDispatch();
	const assets = useSelector(state => state.assets);
	const { user } = useSelector(state => state.user);
	const { company, fullCompantData } = useSelector(state => state.company);
	const {
		initialAssets = [],
		selectedAssets = [],
		sortProps = {
			sortAssetsName: 'vehicle_name',
			sortAssetsOrder: 'asc',
		}
	} = assets;
	const { sortAssetsName, sortAssetsOrder } = sortProps;

	const userRole = user.roles[0];
	const [searchText, setSearchText] = useState('');
	const [devices, setDevices] = useState([]);
	const [showUpdateProfile, setShowUpdateProfile] = useState(false);
	const [showFormatStorage, setShowFormatStorage] = useState(false);
	const [showDevicesMigrationModal, setShowDevicesMigrationModal] = useState(false);

	const [columns, setColumns] = useState(JSON.parse(localStorage.getItem('vehicleColumns')) || {
		channelStatus: true,
		group: true,
		configProfile: true,
		onlineStatus: true,
		totalDataUsage: true,
		streamDataUsage: false,
		firstConnected: false,
		id: false,
		vinNumber: false,
		plateNumber: false,
		imei: false,
		auditLog: false,
		no_sd_card: false,
		sdcard_wear_level: false,
	});
	const columnsLabels = {
		channelStatus: i18n.t('manageVehiclesPage.manageAssetsTable.Channel_Status'),
		group: i18n.t('manageVehiclesPage.manageAssetsTable.Group'),
		configProfile: i18n.t('manageVehiclesPage.manageAssetsTable.Settings_Profile'),
		onlineStatus: i18n.t('manageVehiclesPage.manageAssetsTable.Activity_Status'),
		totalDataUsage: i18n.t('manageVehiclesPage.manageAssetsTable.Data_Usage'),
		streamDataUsage: i18n.t('manageVehiclesPage.manageAssetsTable.Stream_Data_Usage'),
		firstConnected: i18n.t('manageVehiclesPage.manageAssetsTable.First_Connected'),
		id: i18n.t('manageVehiclesPage.manageAssetsTable.ID'),
		vinNumber: i18n.t('manageVehiclesPage.manageAssetsTable.VIN'),
		plateNumber: i18n.t('manageVehiclesPage.manageAssetsTable.Plate_Number'),
		imei: i18n.t('manageVehiclesPage.manageAssetsTable.IMEI'),
		auditLog: i18n.t('manageVehiclesPage.manageAssetsTable.Audit_Log'),
		no_sd_card: i18n.t('manageVehiclesPage.manageAssetsTable.SD_Card_Removal'),
		sdcard_wear_level: i18n.t('manageVehiclesPage.manageAssetsTable.SD_Card_Removal'),
	}
	const [devicesOfflineDisabled, setDevicesOfflineDisabled] = useState('');
	const monthlyDataAvailable = (fullCompantData?.data_usage?.available_data_pool || 0) / 1024

	const toggleLoader = useCallback((show) => {
		dispatch({ type: 'TOGGLE_LOADER', payload: show });
	}, [dispatch]);

	const updateHasUnacknowledgedAlerts = useCallback((state) => {
		dispatch({ type: 'SET_HAS_UNACKNOWLEDGED_ALERTS', payload: state })
	}, [dispatch]);

	const updateLastViewedAssetsList = useCallback((state) => {
		dispatch({ type: 'SET_LAST_VIEWED_ASSETS', payload: state })
	}, [dispatch]);

	const setInitialAssets = useCallback((payload) => {
		dispatch({ type: 'SET_INITIAL_ASSETS', payload })
	}, [dispatch]);

	const setSelectedAssets = useCallback((payload) => {
		dispatch({ type: 'SET_SELECTED_ASSETS', payload })
	}, [dispatch]);

	const setFullCompanyData = useCallback((payload) => {
		dispatch({ type: 'SET_FULL_COMPANY_DATA', payload })
	}, [dispatch]);

	const setSortProps = useCallback((payload) => {
		dispatch({ type: 'SET_SORT_PROPS', payload })
	}, [dispatch]);

	const handleExportToCSV = () => {
		if (assetsContainerRef.current) {
			assetsContainerRef.current.exportToCSV();
		}
	};

	useEffect(() => {
		setSearchText('');
		getCompanyDevices();
		setLastViewedAssetsList();

		return () => {
			setLastViewedAssetsList();
			setSelectedAssets([]);
		}
	}, [company.company_id]);

	const getCompanyDevices = () => {
		toggleLoader(true);

		if (!company || (!company.name || !company.company_id)) {
			toggleLoader(false)
			return
		}

		const requestArray = []
		requestArray.push(getCompanyById(userRole, company.company_id, 'data_usage'))
		requestArray.push(getDevicesList(company.company_id, 'inactive_devices,latest_hdderror,data_usage,ignition,sd_status,channel_status,driver,live_stream,no_sd_card,camera_health,channel_status,sd_wear'))

		// The Device Health tab will have a red dot (bubble) indicator when there are unacknowledged errors on any camera, FWSD-6457
		let hasUnacknowledgedAlerts = false
		Promise.all(requestArray)
			.then(res => {
				const companyData = res[0].data.response.company;
				const initDevices = res[1].data.response.devices.map((item) => {
					item.cameraError = (item.channel_status && item.channel_status.length && item.channel_status.find(channel => channel.fail_ts > (channel.success_ts || '1970-01-01 00:00:00') && item.channel_available.includes(channel.channel))) ? 1 : 0;
					item.recordingError = (!_.isEmpty(item.latest_hdderror) && item.latest_hdderror.count) > 0 ? 1 : 0
					item.liveStreamError = item.live_stream.duration_month > 6000 ? 1 : 0
					if (
						!hasUnacknowledgedAlerts && item.camera_health &&
						((isInternalUser(userRole) && item.camera_health.unacknowledged > 0) || item.camera_health.unacknowledged_sd20 > 0)
					) {
						hasUnacknowledgedAlerts = true
					}
					item.status = detectDeviceStatus(item)
					return item;
				});
				setFullCompanyData(companyData);
				setInitialAssets(initDevices);
				updateHasUnacknowledgedAlerts(hasUnacknowledgedAlerts)
				searchText ? dataSearch() : setDevices(initDevices);
				toggleLoader(false)
			})
			.catch(error => {
				toggleLoader(false)
				toastr.error(t('toastrIssueGettingVehicles'))
			})
	}

	const dataSearch = (e) => {
		const value = e ? e.target.value.toLowerCase() : searchText;
		const searchedDevices = _.cloneDeep(initialAssets).filter(device => {
			return (
				(device.device_id && device.device_id.toString().toLowerCase().includes(value))
				|| (device.vehicle_name && device.vehicle_name.toLowerCase().includes(value))
				|| (device.group_name && device.group_name.toLowerCase().includes(value))
				|| (device.vin_number && device.vin_number.toLowerCase().includes(value))
				|| (device.serial_number && device.serial_number.toLowerCase().includes(value))
				|| (device.tag_plate && device.tag_plate.toLowerCase().includes(value))
			)
		})

		setDevices(searchedDevices)
		setSearchText(value)
	}

	const toggleColumn = (column) => {
		const { key, isSelected } = column;
		sendGA({
			category: userRole,
			action: 'Select Column',
			label: (isSelected ? 'Add ' : 'Remove ') + key
		})
		setColumns(prev => {
			const updated = { ...prev, [key]: !isSelected };
			localStorage.setItem('vehicleColumns', JSON.stringify(updated));
			return updated;
		});
	}

	const selectAssets = (row) => {
		sendGA({
			category: userRole,
			action: 'Toggle Devices',
			label: 'Select One Device'
		})

		selectedAssets.includes(row.device_id) ?
			setSelectedAssets(selectedAssets.filter((id) => id !== row.device_id)) :
			setSelectedAssets([...selectedAssets, row.device_id])
	}

	const selectAllDevices = (event) => {
		const { target } = event;
		const checked = target.type === 'checkbox' ? target.checked : target.value;
		let value = 'Deselect';

		let selectedAssets = []
		if (checked) {
			devices.map(device => { if (!!device.activated_timestamp) selectedAssets.push(device.device_id) });
			value = 'Select';
		}

		sendGA({
			category: userRole,
			action: 'Toggle Devices',
			label: value + ' All Devices'
		})

		setSelectedAssets(selectedAssets)
	}

	const handleActionButton = (modalType) => {
		let value = '';

		if (selectedAssets.length) {
			switch (modalType) {
				case 'showUpdateProfile':
					setShowUpdateProfile(true)
					value = 'Open Update Profile'
					break;
				case 'showFormatStorage':
					setShowFormatStorage(true)
					value = 'Open Format SD'
					break;
				default:
					break;
			}
			sendGA({
				category: userRole,
				action: 'Manage Vehicles Modal',
				label: value,
			})
		}
	}

	const clearSelection = () => {
		setShowUpdateProfile(false)
		setSelectedAssets([])
		checkingDevicesOfflineDisabled()
	}

	const filterColumn = (e, column, value) => {
		let newDevices = [];
		if (column === 'clear_all') {
			if (!devices.length) {
				setDevices(initialAssets);
			} else {
				setDevices([]);
			}
			sendGA({
				category: userRole,
				action: 'Filter Column',
				label: 'Clear All'
			})
			return
		}

		if (!e.target.checked) {
			if (column === 'latest_hdderror') {
				value === 'Needs Reformat'
					? newDevices = devices.filter(e => _.isEmpty(e.latest_hdderror) || e.latest_hdderror.count === 0)
					: newDevices = devices.filter(e => !_.isEmpty(e.latest_hdderror) && e.latest_hdderror.count > 0)
			} else if (column === 'group_name') {
				newDevices = devices.filter(e => value === 'No group' ? (e.group_name && e.group_name.length) : e.group_name !== value);
			} else {
				newDevices = devices.filter(e => e[column] !== value);
			}
			e.target.checked = true;
		} else {
			if (column === 'latest_hdderror') {
				newDevices = [...devices];
				value === 'Needs Reformat' ?
					newDevices.unshift(...initialAssets.filter(e => !_.isEmpty(e.latest_hdderror) && e.latest_hdderror.count > 0)) :
					newDevices.unshift(...initialAssets.filter(e => _.isEmpty(e.latest_hdderror) || e.latest_hdderror.count === 0))
			} else if (column === 'group_name') {
				newDevices = [...initialAssets.filter(e => value === 'No group' ? (!e.group_name || !e.group_name.length) : e.group_name === value), ...devices];
			} else {
				newDevices = [...initialAssets.filter(e => e[column] === value), ...devices];
			}
			e.target.checked = false;
		}

		sendGA({
			category: userRole,
			action: 'Filter Column',
			label: column + ': ' + value
		})
		setDevices(newDevices)
	}

	const filterConfigColumn = (e, value) => {
		let newDevices = [];
		if (!e.target.checked) {
			if (value !== 'Manual' && value !== 'Pending') {
				newDevices = devices.filter(e => e['uploaded_company_profile'] !== value);
			} else if (value === 'Pending') {
				newDevices = devices.filter(e => e['config_checksum'] !== 1)
			} else {
				newDevices = devices.filter(e => (e['config_checksum'] === 1 || (e['uploaded_company_profile'] !== 'Manual' && e['uploaded_company_profile'])))
			}
		} else {
			if (value !== 'Manual' && value !== 'Pending') {
				newDevices = [...initialAssets.filter(e => e['uploaded_company_profile'] === value), ...devices];
			} else if (value === 'Pending') {
				newDevices = [...initialAssets.filter(e => e['config_checksum'] === 1), ...devices];
			} else {
				newDevices = [...initialAssets.filter(e => (e['config_checksum'] !== 1 && (e['uploaded_company_profile'] === 'Manual' || !e['uploaded_company_profile']))), ...devices];
			}
		}
		setDevices(newDevices)
	}

	const setLastViewedAssetsList = () => {
		if (company.company_id) {
			const { lastViewedVehiclesPage } = assets;
			const obj = lastViewedVehiclesPage.find(({ companyId }) => companyId === company.company_id)
			if (obj) {
				obj.ts = moment.utc().format('YYYYMMDDHHmmss')
			} else {
				lastViewedVehiclesPage.push({ companyId: company.company_id || '', ts: moment.utc().format('YYYYMMDDHHmmss') })
			}
			updateLastViewedAssetsList([...lastViewedVehiclesPage])
		}
	}

	const setAssetsSort = (sortName, sortOrder) => {
		setSortProps({
			sortAssetsName: sortName,
			sortAssetsOrder: sortOrder,
		});
	}

	const formatSDUser = ['fleet_manager', 'fleet_maintainer', 'group_manager', 'customer_service', 'partner', 'system_admin'].indexOf(userRole) > -1;

	const isSingleUsers = () => {
		const singleUsersList = ['fleet_maintainer', 'fleet_manager', 'group_manager', 'user', 'installer', 'partner_view', 'sales_demo', 'storage_manager', 'storage_user', 'custom_user', 'custom_partner']
		return singleUsersList.indexOf(userRole) !== -1
	}

	const checkingDevicesOfflineDisabled = () => {
		const devices = initialAssets.filter(({ device_id, status }) => selectedAssets.includes(device_id) && ['disabled', 'offline'].includes(status))

		if (devices.length) {
			setDevicesOfflineDisabled(devices.map(({ vehicle_name, device_id }) => vehicle_name || device_id))
		}
	}

	const userAccess = ['system_admin', 'customer_service', 'partner', 'installer', 'partner_view', 'sales_demo'].includes(userRole) || (['custom_partner'].includes(userRole) && isCustomUserPermissions(userRole, user.permissions, 'vehicle_edit'));

	return (
		<div className="manage-vehicles">
			<div className="page-subheader manage-vehicles-subheader">
				<div className="subheader-section search">
					<SearchField term={searchText} onSearch={dataSearch} placeholder={t('placeholderSearchVehicles')} />
				</div>
				<div className="subheader-assets-btn-section">
					<div className="subheader-section button-group">
						<CustomButton
							onClick={() => handleActionButton('showUpdateProfile')}
							disabled={!selectedAssets.length}
							variant='toggle-group'
						>
							{t('btnUpdateProfile')}
						</CustomButton>
						{formatSDUser &&
							<CustomButton
								onClick={() => handleActionButton('showFormatStorage')}
								disabled={!selectedAssets.length}
								variant='toggle-group'
								style={{ marginLeft: '2px' }}
							>
								{t('btnFormatSD')}
							</CustomButton>
						}
						{userAccess &&
							<CustomButton
								onClick={() => setShowDevicesMigrationModal(true)}
								disabled={!selectedAssets.length}
								variant='toggle-group'
								style={{ marginLeft: '2px' }}
							>
								{t('btnMigrate')}
							</CustomButton>
						}
					</div>
					<div className="subheader-section filter-section">
						<CustomButton
							onClick={handleExportToCSV}
							size='sm'
							variant='primary-outline'
							prefix='export-button'
						>
							{exportIcon}
							<span className='export-label'>{t('titleExport')}</span>
						</CustomButton>
						<FilterMenu
							columns={columns}
							columnsLabels={columnsLabels}
							toggleColumn={toggleColumn}
							userRole={userRole}
							user={user}
							showClose={true}
							twoColumns
						/>
					</div>
				</div>
			</div>
			<main className="main-content-block">
				<h3 className="category-title">{t('titleAllAssets')}</h3>

				{!isSingleUsers()
					&& (
						<AddAssetContainer changedDevicesList={getCompanyDevices} devices={devices} />
					)}

				<div className="vehicle-table-header">
					<CustomButton variant="link"
						onClick={() => setSelectedAssets([])}>{t('textClear')} {selectedAssets ? ('(' + selectedAssets.length + ')') : ''}</CustomButton>
				</div>
				<ManageAssetsContainer
					ref={assetsContainerRef}
					devices={devices}
					initialAssets={initialAssets}
					companyData={fullCompantData}
					columns={columns}
					selectedAssets={selectedAssets}
					selectAssets={selectAssets}
					selectAllDevices={selectAllDevices}
					filterColumn={filterColumn}
					filterConfigColumn={filterConfigColumn}
					selectProfile={selectProfile}
					sortAssetsName={sortAssetsName}
					sortAssetsOrder={sortAssetsOrder}
					setAssetsSort={setAssetsSort}
					getCompanyDevices={getCompanyDevices}
					monthlyDataAvailable={monthlyDataAvailable}
				/>

				{showUpdateProfile && (
					<ModalChangeConfBatch
						userRole={userRole}
						companyData={fullCompantData}
						selectedAssets={selectedAssets}
						clearSelection={clearSelection}
						onHide={() => setShowUpdateProfile(false)}
						getCompanyDevices={getCompanyDevices}
					/>
				)}

				{showFormatStorage && (
					<ModalFormatSDBatch
						userRole={userRole}
						selectedAssets={selectedAssets}
						clearSelection={clearSelection}
						onHide={() => setShowFormatStorage(false)}
						getCompanyDevices={getCompanyDevices}
					/>
				)}

				{devicesOfflineDisabled.length > 0 &&
					<ModalDeviceOfflineDisabled
						devicesOfflineDisabled={devicesOfflineDisabled}
						onHide={() => setDevicesOfflineDisabled('')}
					/>
				}

				{showDevicesMigrationModal && (
					<ModalDevicesMigration
						user={user}
						companyData={fullCompantData}
						selectedDevices={selectedAssets}
						onHide={() => setShowDevicesMigrationModal(false)}
						getCompanyDevices={getCompanyDevices}
					/>
				)}
			</main>
		</div>
	);
}

export default ManageAssets;
